import PropTypes from "prop-types"
import React from "react"
import { useMarina } from "src/contexts/MarinaContext"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import { methodWithReference, paymentIcon } from "./helpers"

const REVERSED_REASONS = {
  failed: "payment failed",
  dispute_lost: "dispute lost",
}

const Stat = ({ label, children, detail }) => (
  <div className="min-w-0">
    <div className="text-sm text-gray-700">{label}</div>
    <div className="mt-1 whitespace-nowrap text-2xl font-bold text-gray-900">
      {children}
    </div>
    {detail && <div className="mt-1 text-sm text-gray-700">{detail}</div>}
  </div>
)

Stat.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  detail: PropTypes.string,
}

const PaymentStats = ({ payment }) => {
  const { presentmentCurrency } = useMarina()
  const money = (amount) =>
    formatCurrencyFromMinorUnits({ amount, currency: presentmentCurrency })

  return (
    <section
      aria-label="Payment summary"
      className="flex flex-col gap-6 border-t border-gray-200 pt-[16px] md:flex-row md:flex-wrap md:gap-x-[60px]"
    >
      <Stat label="Method">
        <span className="flex max-w-[420px] items-center">
          <i
            className={`${paymentIcon(payment)} mr-3 text-gray-700`}
            aria-hidden="true"
          />
          <span className="truncate" title={methodWithReference(payment)}>
            {methodWithReference(payment)}
          </span>
        </span>
      </Stat>
      <Stat
        label="Amount paid"
        detail={
          payment.surchargeCents != null
            ? `Includes ${money(payment.surchargeCents)} card surcharge`
            : undefined
        }
      >
        {money(payment.paymentTotalCents)}
      </Stat>
      {(payment.refundedCents > 0 || payment.refundPendingCents > 0) && (
        <Stat
          label="Refunded"
          detail={
            payment.refundPendingCents > 0
              ? `${money(payment.refundPendingCents)} pending`
              : undefined
          }
        >
          {money(payment.refundedCents)}
        </Stat>
      )}
      <Stat
        label="Net received"
        detail={
          payment.reversedCents > 0
            ? `Reversed: ${REVERSED_REASONS[payment.status]}`
            : undefined
        }
      >
        {money(payment.netReceivedCents)}
      </Stat>
    </section>
  )
}

PaymentStats.propTypes = {
  payment: PropTypes.shape({
    status: PropTypes.string.isRequired,
    paymentTotalCents: PropTypes.number.isRequired,
    surchargeCents: PropTypes.number,
    refundedCents: PropTypes.number.isRequired,
    refundPendingCents: PropTypes.number.isRequired,
    reversedCents: PropTypes.number.isRequired,
    netReceivedCents: PropTypes.number.isRequired,
    paymentMethodType: PropTypes.string,
    offlinePaymentType: PropTypes.string,
  }).isRequired,
}

export default PaymentStats
